import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { isSafeImage } from "../../../utils/brandApi";

export default function LogoPickerModal({ open, onClose, logos = [], onSelect, onOpenAccount, error = "" }) {
  const { t } = useTranslation();

  useEffect(() => {
    if (!open) return;
    const onEsc = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black-900 bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-lg">{t("logoEditor.pickLogo")}</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X size={18} />
          </button>
        </div>

        <div className="space-y-4 mb-4">
          {logos.map((logo) => (
            <div key={logo.target} className="space-y-2">
              <p className="text-xs font-bold uppercase tracking-wide text-black-400">{logo.target}</p>
              <div className="grid grid-cols-2 gap-3">
                {isSafeImage(logo.logo_color) && (
                  <button
                    type="button"
                    onClick={() => onSelect?.(logo.logo_color)}
                    className="flex flex-col items-center gap-2 p-3 border-2 border-gray-200 rounded-xl hover:border-primary-300 transition-colors"
                  >
                    <div className="w-full h-16 bg-white flex items-center justify-center">
                      <img src={logo.logo_color} alt="color" className="max-h-full max-w-full object-contain" />
                    </div>
                    <span className="text-xs font-bold text-black-600">{t("userAccountModal.logos.colorVersion")}</span>
                  </button>
                )}
                {isSafeImage(logo.logo_negative) && (
                  <button
                    type="button"
                    onClick={() => onSelect?.(logo.logo_negative)}
                    className="flex flex-col items-center gap-2 p-3 border-2 border-gray-200 rounded-xl hover:border-primary-300 transition-colors"
                  >
                    <div className="w-full h-16 bg-black-800 flex items-center justify-center">
                      <img src={logo.logo_negative} alt="negative" className="max-h-full max-w-full object-contain" />
                    </div>
                    <span className="text-xs font-bold text-black-600">{t("userAccountModal.logos.negativeVersion")}</span>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {logos.length === 0 && (
          <p className="text-sm text-black-600 mb-4">{t("logoEditor.noLogos")}</p>
        )}

        {error && <p className="text-sm text-red-500 mb-3">{error}</p>}

        <button
          type="button"
          onClick={() => { onClose?.(); onOpenAccount?.(); }}
          className="text-sm font-bold underline text-black-800 hover:text-black"
        >
          {t("logoEditor.manageLogos")} →
        </button>
      </div>
    </div>
  );
}